import { Cell, Row } from '@tanstack/react-table';
import { Apartment, ApartmentStatus } from '../CommonTypes';
import { useAtom } from 'jotai';
import {
  apartmentOnTopState,
  currentApartmentAtom,
  currentSlideAtom,
  hooveredApartmentId,
  isMobile,
} from '../states/planner';
import StatusCell from './StatusCell';
import { useApartment } from '../hooks/useApartment';

class VirtualRow {
  id: string;
  apartmentid: string;
  cells: Cell<Apartment, unknown>[];
  producturl?: string;
  apartmentOnTop?: Apartment;
  mainview?: number;
  firebaseId?: string;

  constructor(
    id: string,
    apartmentid: string,
    cells: Cell<Apartment, unknown>[],
    producturl?: string,
    apartmentOnTop?: Apartment,
    mainview?: number,
    firebaseId?: string
  ) {
    this.id = id;
    this.apartmentid = apartmentid;
    this.cells = cells;
    this.producturl = producturl;
    this.apartmentOnTop = apartmentOnTop;
    this.mainview = mainview;
    this.firebaseId = firebaseId;
  }

  getVisibleCells(): Cell<Apartment, unknown>[] {
    return this.cells;
  }

  getCellValue(cell: Cell<Apartment, unknown>) {
    if (this.apartmentOnTop) {
      const key = cell.column.id.toString();
      return this.apartmentOnTop[key as keyof Apartment];
    }

    return cell.getValue();
  }
}

const TableApartmentsRow = ({ row, isTopRow }: { row: Row<Apartment>; isTopRow?: boolean }) => {
  const [overApartmentId, setOverApartmentId] = useAtom(hooveredApartmentId);
  const [apartmentOnTop, setApartmentOnTop] = useAtom(apartmentOnTopState);
  const [currentSlide, setCurrentSlide] = useAtom(currentSlideAtom);
  const [currentApartment, setCurrentApartment] = useAtom(currentApartmentAtom);
  const apartment = useApartment();

  if (!row) return null;
  if (!isTopRow && apartmentOnTop?.apartmentid === row.getValue('apartmentid')) return null;

  let vRow: VirtualRow;
  if (isTopRow) {
    if (!apartmentOnTop) return null;

    vRow = new VirtualRow(
      'toprow',
      apartmentOnTop.apartmentid,
      row.getVisibleCells(),
      apartmentOnTop.producturl,
      apartmentOnTop,
      row.getValue('mainview'),
      apartmentOnTop.id
    );
  } else {
    vRow = new VirtualRow(
      row.id,
      row.getValue('apartmentid'),
      row.getVisibleCells(),
      row.original.producturl,
      undefined,
      row.getValue('mainview'),
      row.original.id
    );
  }

  const classNames = [
    'tr-hooverable',
    'cursor-pointer',
    vRow.apartmentid === overApartmentId || isTopRow ? 'highlight-row' : '',
  ];

  const viewEvents = isTopRow
    ? {}
    : {
        onFocus: () => {
          setApartmentOnTop(null);
          setOverApartmentId(vRow.apartmentid);
        },
        onMouseOver: () => {
          setApartmentOnTop(null);
          setOverApartmentId(vRow.apartmentid);
        },
        onMouseOut: () => {
          if (!isMobile()) {
            setApartmentOnTop(null);
            setOverApartmentId(null);
          }
        },
        onBlur: () => {
          setApartmentOnTop(null);
          setOverApartmentId(null);
        },
        onClick: () => {
          {
            setApartmentOnTop(null);
            setOverApartmentId(vRow.apartmentid);
            if (currentApartment) {
              setCurrentApartment(row.original);
            }
            if (vRow.mainview !== undefined) {
              if (!isMobile()) {
                if (currentSlide !== vRow.mainview) {
                  setTimeout(() => {
                    setCurrentApartment(row.original);
                  }, 800);
                } else {
                  setCurrentApartment(row.original);
                }
              }
              setCurrentSlide(vRow.mainview);
            }
          }
        },
      };

  const hideWhenMobile = (columnName: string) => {
    if (columnName === 'apartmentid') {
      return '';
      // return 'hidewhenmobile';
    } else if (columnName === 'floor') {
      return '';
      // return 'hidewhenminimobile';
    } else if (columnName === 'mainview') {
      return 'alwayshide';
    }
    return '';
  };

  const handleStatusChange = (value: 'sold' | 'available' | 'reserved' | 'extra') => {
    if (!vRow.firebaseId) return;

    apartment.mutate({
      id: vRow.firebaseId,
      data: { status: value },
    });
  };

  return (
    <tr className={classNames.join(' ')} key={vRow.id} {...viewEvents}>
      {vRow.getVisibleCells().map(cell => {
        const cellId = cell.column.id.toString();
        const value = vRow.getCellValue(cell);

        let elementValue = <></>;

        if (cellId === 'status') {
          elementValue = (
            <StatusCell
              status={value as Apartment['status']}
              onChange={(value: ApartmentStatus) => {
                handleStatusChange(value);
              }}
            />
          );
        } else if (cellId === 'price') {
          elementValue = (
            <span className="whitespace-nowrap">
              {typeof value === 'number' && value > 0 ? value.toLocaleString('sv-SE') + ' kr' : '-'}
            </span>
          );
        } else if (cellId === 'charge') {
          elementValue = (
            <span className="whitespace-nowrap">
              {typeof value === 'number' && value > 0 ? value.toLocaleString('sv-SE') + ' kr/mån' : '-'}
            </span>
          );
        } else if (cellId === 'size') {
          elementValue = (
            <span className="whitespace-nowrap">
              {(value as Apartment['size']).toLocaleString('sv-SE') + ' m'}
              <sup>2</sup>
            </span>
          );
        } else if (cellId === 'lotsize') {
          elementValue = (
            <span className="whitespace-nowrap">
              {(value as Apartment['lotsize']).toLocaleString('sv-SE') + ' m'}
              <sup>2</sup>
            </span>
          );
        } else if (cellId === 'moveindate') {
          elementValue = (
            <span className="whitespace-nowrap">
              {' '}
              {(value as Apartment['moveindate'])?.toDate()?.toLocaleString('sv-SE', { dateStyle: 'short' })}
            </span>
          );
        } else {
          elementValue = <span className="whitespace-nowrap">{value as string}</span>;
        }

        return (
          <td
            {...{
              className: `pr-3 ${hideWhenMobile(cellId)} ${['status'].find(s => s == cellId) ? 'text-left' : 'text-right'}`,
              // className: cellId === 'apartmentid' || cellId === 'floor' ? 'hidewhenmobile pr-3' : 'pr-3',
            }}
            key={cell.id}
          >
            {elementValue}
          </td>
        );
      })}
    </tr>
  );

  return <></>;
};

export default TableApartmentsRow;
